import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../components/shared/ToastNotification';

// Premium Staff Form - Sophisticated Onboarding
function StaffForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const isEdit = !!id;

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        role_id: '',
        active: true
    });

    const [roles, setRoles] = useState<any[]>([]);
    const [staffMember, setStaffMember] = useState<any>(null);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadRoles();
        if (isEdit) {
            loadStaffMember();
        }
    }, [id]);

    const loadRoles = async () => {
        try {
            const response = await api.get('/settings/roles');
            const rolesData = Array.isArray(response.data) ? response.data : (response.data?.roles || response.data?.data || []);
            setRoles(rolesData);
        } catch (err) {
            console.error('Error loading roles:', err);
        }
    };

    const loadStaffMember = async () => {
        try {
            const response = await api.get(`/settings/staff/${id}`);
            if (response.success) {
                const member = response.data;
                setStaffMember(member);
                setFormData({
                    email: member.user?.email || '',
                    role_id: member.role?.id || '',
                    active: member.active
                });
            } else {
                const msg = response.message || 'Verification failed for profile retrieval';
                setError(msg);
                toast.error(msg);
            }
        } catch (err: any) {
            const msg = err.message || 'Error fetching staff member';
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setSaving(true);
            setError(null);

            const response = isEdit
                ? await api.put(`/settings/staff/${id}`, formData)
                : await api.post('/settings/staff', formData);

            if (response.success) {
                toast.success(`Personnel ${isEdit ? 'updated' : 'onboarded'} successfully`);
                navigate('/staff');
            } else {
                const backendErrors = response.errors?.length > 0
                    ? response.errors
                    : [response.message || response.error || 'System error during personnel update'];
                
                setError(backendErrors.join(", "));
                toast.error(backendErrors[0]);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (err: any) {
            const msg = err.message || 'Critical communication error';
            setError(msg);
            toast.error(msg);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
                <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                <div className="text-slate-500 font-black tracking-widest uppercase text-[10px]">Accessing Personnel Profile...</div>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header */}
            <div className="flex items-center justify-between mb-10">
                <div>
                    <h1 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
                        {isEdit ? 'Refine' : 'Add'} <span className="text-indigo-600">Personnel</span>
                    </h1>
                    <p className="text-slate-500 mt-1 font-medium italic">Assign roles and manage workspace access</p>
                </div>
                <Link to="/staff" className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </Link>
            </div>

            <div className="bg-white rounded-lg border border-slate-100 shadow-2xl shadow-slate-200/50 p-8 sm:p-12">
                <form onSubmit={handleSubmit} className="space-y-10">
                    {error && (
                        <div className="bg-rose-50 border-l-4 border-rose-500 p-5 rounded-2xl shadow-sm animate-bounce-short">
                            <div className="flex gap-3 items-center">
                                <svg className="w-5 h-5 text-rose-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <p className="text-sm font-black text-rose-800 tracking-wide uppercase">{error}</p>
                            </div>
                        </div>
                    )}

                    {isEdit && staffMember && (
                        <div className="flex items-center gap-6 p-6 bg-slate-50 border border-slate-100 rounded-3xl mb-4 animate-in zoom-in-95">
                            <div className="w-16 h-16 bg-white text-indigo-600 rounded-2xl flex items-center justify-center font-black text-2xl shadow-sm ring-1 ring-slate-100">
                                {staffMember.user?.name?.charAt(0)}
                            </div>
                            <div>
                                <h3 className="font-black text-slate-900 text-lg">{staffMember.user?.name}</h3>
                                <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">{staffMember.user?.email}</p>
                            </div>
                        </div>
                    )}

                    <div className="space-y-12">
                        {!isEdit && (
                            <div className="space-y-6">
                                <h3 className="text-[10px] font-black text-indigo-600 uppercase tracking-widest flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                                    Personnel Identity
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] pl-1">Full Name</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-3xl outline-none focus:bg-white focus:border-indigo-500 transition-all font-black text-slate-900 shadow-sm"
                                            placeholder="Enter Full Name"
                                        />
                                        <p className="text-[10px] text-slate-400 font-bold italic pl-1">Required to create new account if user not found</p>
                                    </div>
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] pl-1">Professional Email</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-3xl outline-none focus:bg-white focus:border-indigo-500 transition-all font-black text-slate-900 shadow-sm"
                                            placeholder="associate@firm.com"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="space-y-6">
                            <h3 className="text-[10px] font-black text-emerald-600 uppercase tracking-widest flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                                Organizational Access
                            </h3>
                            <div className="grid grid-cols-1 gap-8">
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] pl-1">Designated Role</label>
                                    <div className="relative">
                                        <select
                                            name="role_id"
                                            value={formData.role_id}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-3xl outline-none focus:bg-white focus:border-indigo-500 transition-all font-black text-slate-700 shadow-sm appearance-none cursor-pointer"
                                        >
                                            <option value="" disabled>Select Role</option>
                                            {roles.map(role => (
                                                <option key={role.id} value={role.id}>{role.name}</option>
                                            ))}
                                        </select>
                                        <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="pt-4">
                            <label className="flex items-center gap-4 cursor-pointer group w-fit">
                                <div className="relative">
                                    <input
                                        type="checkbox"
                                        name="active"
                                        checked={formData.active}
                                        onChange={handleChange}
                                        className="sr-only"
                                    />
                                    <div className={`w-14 h-8 rounded-full transition-colors duration-300 ${formData.active ? 'bg-indigo-500' : 'bg-slate-200'}`}></div>
                                    <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform duration-300 shadow-sm ${formData.active ? 'translate-x-6' : ''}`}></div>
                                </div>
                                <div>
                                    <p className="text-sm font-black text-slate-900 group-hover:text-indigo-600 transition-colors">Operational Status</p>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Toggle immediately grants or revokes workspace access</p>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-4 pt-10 border-t border-slate-50">
                        <Link
                            to="/staff"
                            className="px-8 py-5 bg-white border border-slate-200 text-slate-500 rounded-3xl font-black uppercase tracking-[0.2em] text-[10px] transition-all hover:bg-slate-50 text-center"
                        >
                            Back to Roster
                        </Link>
                        <button
                            type="submit"
                            disabled={saving}
                            className={`px-12 py-5 bg-slate-900 text-white rounded-3xl font-black uppercase tracking-[0.2em] text-[10px] transition-all shadow-2xl shadow-indigo-100 hover:bg-black hover:scale-105 active:scale-95 ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {saving ? 'Syncing...' : (isEdit ? 'Update Access' : 'Onboard Member')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default StaffForm;
